import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  AlertTitle,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
} from '@mui/material'
import {
  Download,
  Lightbulb,
  AutoAwesome,
  ContentCopy,
  Warning,
  CheckCircle,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { documentoService } from '@/services/documentoService'
import { AnaliseIaResponse, Documento } from '@/types/documento'
import {
  corFormato,
  corRisco,
  formatarTamanho,
  formatoDoMime,
  iconeFormato,
  normalizarRisco,
  rotuloRisco,
} from './formatoArquivo'

const formatDate = (dateString: string | undefined): string =>
  dateString ? dayjs(dateString).format('DD/MM/YYYY HH:mm') : '—'

/**
 * O `/conteudo` e o `/analise` respondem 404 enquanto o documento não foi processado —
 * situação NORMAL, não erro. Só o documento em si é obrigatório; as duas partes
 * opcionais viram `null` e a tela explica o que falta.
 */
async function opcional<T>(promessa: Promise<T>): Promise<T | null> {
  try {
    return await promessa
  } catch {
    return null
  }
}

const DocumentoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()

  const [documento, setDocumento] = useState<Documento | null>(null)
  const [texto, setTexto] = useState<string | null>(null)
  const [analise, setAnalise] = useState<AnaliseIaResponse | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)
  const [reprocessando, setReprocessando] = useState(false)

  const carregar = useCallback(async () => {
    if (!id) {
      setErro('Endereço inválido: falta o identificador do documento.')
      setCarregando(false)
      return
    }
    setCarregando(true)
    setErro(null)
    try {
      const doc = await documentoService.buscar(id)
      setDocumento(doc)
      const [conteudo, ia] = await Promise.all([
        opcional(documentoService.obterConteudo(id)),
        opcional(documentoService.obterAnalise(id)),
      ])
      setTexto(conteudo?.conteudoTexto ?? null)
      setAnalise(ia)
    } catch {
      setErro('Não foi possível carregar este documento. Ele pode ter sido removido.')
    } finally {
      setCarregando(false)
    }
  }, [id])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const handleCopyText = () => {
    if (!texto) return
    void navigator.clipboard.writeText(texto)
    setCopiado(true)
    enqueueSnackbar('Texto copiado para a área de transferência!', { variant: 'success' })
    setTimeout(() => setCopiado(false), 2000)
  }

  const handleDownload = async () => {
    if (!documento) return
    try {
      // `baixarArquivo` traz os bytes pelo backend. A URL pré-assinada do outro endpoint
      // aponta para o host interno do MinIO, inalcançável pelo browser.
      const blob = await documentoService.baixarArquivo(documento.id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = documento.nome
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      enqueueSnackbar('Falha ao baixar o arquivo.', { variant: 'error' })
    }
  }

  const handleReprocessar = async () => {
    if (!documento) return
    setReprocessando(true)
    try {
      await documentoService.reprocessar(documento.id)
      enqueueSnackbar('Reprocessamento iniciado.', { variant: 'success' })
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao solicitar o reprocessamento.', { variant: 'error' })
    } finally {
      setReprocessando(false)
    }
  }

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando documento" />
      </Box>
    )
  }

  if (erro || !documento) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button component={RouterLink} to="/documentos" size="small" color="inherit">
              Voltar
            </Button>
          }
        >
          {erro ?? 'Documento não encontrado.'}
        </Alert>
      </Box>
    )
  }

  const formato = formatoDoMime(documento.mimeType)
  const risco = normalizarRisco(analise?.riscoIdentificado)
  const confianca = analise?.confidenciaIdentificacao ?? null
  const dados = analise?.dadosExtraidos ?? {}
  const resumo = analise?.resumo ?? documento.resumoIa ?? null
  const tipoDetectado = analise?.tipoIdentificado ?? documento.tipoIdentificadoIa ?? null

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink component={RouterLink} to="/documentos" underline="hover" color="primary">
          Documentos
        </MuiLink>
        <Typography color="textPrimary">{documento.nome}</Typography>
      </Breadcrumbs>

      {/*
        Validação documental por IA: alerta, nunca bloqueio — a IA erra, e recusar upload
        por classificação errada seria pior que o bug original (o "bug do Balanço").
      */}
      {documento.tipoDivergente && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>O conteúdo pode não corresponder ao tipo declarado</AlertTitle>
          Este arquivo foi enviado como <strong>{documento.tipo}</strong>, mas a análise de
          IA o identificou como <strong>{documento.tipoIdentificadoIa ?? 'outro tipo'}</strong>.
          Confira o anexo antes de aceitar o documento.
        </Alert>
      )}

      {documento.status === 'ERRO' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Falha no processamento</AlertTitle>
          O texto deste documento não pôde ser extraído. Use “Reprocessar IA” para tentar
          novamente.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, textAlign: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: corFormato(formato),
                fontSize: 40,
                mx: 'auto',
                mb: 2,
              }}
            >
              {iconeFormato(formato)}
            </Avatar>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}>
              {documento.nome}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'left' }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Tipo
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {documento.tipo}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Tamanho
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatarTamanho(documento.tamanhoBytes)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    label={documento.status}
                    color={documento.status === 'PROCESSADO' ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Data de Upload
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(documento.dataUpload ?? documento.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                startIcon={<Download />}
                onClick={handleDownload}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<AutoAwesome />}
                onClick={handleReprocessar}
                disabled={reprocessando}
              >
                {reprocessando ? 'Reprocessando…' : 'Reprocessar IA'}
              </Button>
            </Stack>

            {documento.entidadeReferencia && (
              <>
                <Divider sx={{ my: 2 }} />
                <Card sx={{ p: 1.5, bgcolor: 'primary.light' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Vinculado a:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {documento.tipoEntidade ?? 'Registro'} {documento.entidadeReferencia}
                  </Typography>
                </Card>
              </>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <Card sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Texto Extraído
              </Typography>
              <Chip label="Apache Tika" size="small" variant="outlined" />
            </Box>

            {texto ? (
              <>
                <Box
                  sx={{
                    maxHeight: 600,
                    overflow: 'auto',
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    mb: 2,
                  }}
                >
                  {texto}
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyText}
                  sx={{
                    color: copiado ? 'success.main' : 'default',
                    borderColor: copiado ? 'success.main' : 'default',
                  }}
                >
                  {copiado ? 'Texto Copiado!' : 'Copiar Texto'}
                </Button>
              </>
            ) : (
              <Alert severity="info">
                {documento.status === 'PROCESSANDO' || documento.status === 'PENDENTE'
                  ? 'A extração ainda está em andamento. Recarregue em instantes.'
                  : 'Nenhum texto foi extraído deste arquivo.'}
              </Alert>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {analise ? (
            <>
              <Card sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Lightbulb color="warning" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Classificação IA
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Tipo Detectado
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {tipoDetectado ?? 'Não identificado'}
                  </Typography>
                  {confianca !== null && (
                    <>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        Confiança
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={confianca * 100}
                          sx={{ flex: 1 }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: '45px' }}>
                          {Math.round(confianca * 100)}%
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Card>

              {resumo && (
                <Card sx={{ p: 3, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                    Resumo Executivo
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'textSecondary' }}>
                    {resumo}
                  </Typography>
                </Card>
              )}

              {Object.keys(dados).length > 0 && (
                <Card sx={{ p: 3, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                    Dados Extraídos
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {Object.entries(dados).map(([chave, valor]) => (
                          <TableCell
                            key={chave}
                            colSpan={2}
                            sx={{
                              py: 1,
                              borderBottom: '1px solid #eee',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, color: 'textSecondary' }}
                              >
                                {chave}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {String(valor)}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              )}

              {risco && (
                <Card sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {risco === 'BAIXO' && <CheckCircle sx={{ color: 'success.main' }} />}
                    {risco === 'ALTO' && <Warning sx={{ color: 'error.main' }} />}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Nível de Risco
                    </Typography>
                  </Box>
                  <Chip label={rotuloRisco(risco)} color={corRisco(risco)} sx={{ mb: 1.5 }} />
                  {analise.recomendacoes && analise.recomendacoes.length > 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {analise.recomendacoes.join(' ')}
                    </Typography>
                  )}
                </Card>
              )}
            </>
          ) : (
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Lightbulb color="disabled" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Análise de IA
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Este documento ainda não foi analisado pela IA.
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default DocumentoDetailPage
